import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { CONTROL_SQUARE } from '@/components/ui/control-size';

/**
 * Modal Component - Meridian Dark Design System
 *
 * A fully accessible modal dialog with overlay, animations, and focus management.
 * Built on Radix UI Dialog for robust accessibility and keyboard navigation.
 *
 * @example
 * <Modal>
 *   <ModalTrigger asChild>
 *     <Button>Open Modal</Button>
 *   </ModalTrigger>
 *   <ModalContent>
 *     <ModalHeader>
 *       <ModalTitle>Modal Title</ModalTitle>
 *       <ModalDescription>Modal description text</ModalDescription>
 *     </ModalHeader>
 *     <div>Body content</div>
 *     <ModalFooter>
 *       <Button variant="ghost">Cancel</Button>
 *       <Button>Confirm</Button>
 *     </ModalFooter>
 *   </ModalContent>
 * </Modal>
 */

const Modal = DialogPrimitive.Root;
const ModalTrigger = DialogPrimitive.Trigger;
const ModalPortal = DialogPrimitive.Portal;
const ModalClose = DialogPrimitive.Close;

/**
 * Modal overlay with backdrop blur and fade animation
 */
const ModalOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Base overlay - semi-transparent warm midnight
      'fixed inset-0 z-modal bg-bg/80 backdrop-blur-sm',
      // Animation
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      // Duration
      'duration-150',
      className
    )}
    {...props}
  />
));
ModalOverlay.displayName = DialogPrimitive.Overlay.displayName;

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface ModalContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  size?: ModalSize;
  closeOnOutsideClick?: boolean;
}

/**
 * Modal content container with size variants and animations
 */
const ModalContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ModalContentProps
>(({ className, children, size = 'md', closeOnOutsideClick = true, ...props }, ref) => {
  const { t } = useTranslation();
  // Size classes mapping
  const sizeClasses: Record<ModalSize, string> = {
    sm: 'max-w-[400px]',
    md: 'max-w-[600px]',
    lg: 'max-w-[800px]',
    xl: 'max-w-[1000px]',
    full: 'max-w-[calc(100vw-2rem)] min-h-[calc(100vh-2rem)]',
  };

  return (
    <ModalPortal>
      <ModalOverlay />
      <DialogPrimitive.Content
        ref={ref}
        // Centred in the part of a content-sized embedded frame the portal shows (index.css).
        data-frame-anchor="center"
        onPointerDownOutside={(e) => {
          if (!closeOnOutsideClick) {
            e.preventDefault();
          }
        }}
        className={cn(
          // Positioning
          'fixed left-[50%] top-[50%] z-modal',
          'translate-x-[-50%] translate-y-[-50%]',
          // Sizing
          'w-full',
          sizeClasses[size],
          // Background and borders - elevated with sharp edges
          'bg-bg-surface border border-border shadow-xl',
          'rounded-modal', // Sharp executive edges
          // Padding
          'p-6',
          // Animation
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
          'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          // Duration
          'duration-150',
          // Focus styles
          'focus:outline-none focus:ring-2 focus:ring-primary/30',
          className
        )}
        {...props}
      >
        {children}
        {/* Close button */}
        <DialogPrimitive.Close
          className={cn(
            // Positioning
            'absolute right-4 top-4',
            // Size
            CONTROL_SQUARE.sm,
            // Styling
            'rounded-sm opacity-70',
            'text-fg-muted hover:text-fg hover:bg-bg-subtle',
            // Focus
            'focus:outline-none focus:ring-2 focus:ring-primary/30',
            // Transition
            'transition-all duration-150',
            // Center icon
            'inline-flex items-center justify-center'
          )}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">{t('common:common.close')}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </ModalPortal>
  );
});
ModalContent.displayName = DialogPrimitive.Content.displayName;

/**
 * Modal header container
 */
const ModalHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5', 'mb-4', className)} {...props} />
);
ModalHeader.displayName = 'ModalHeader';

/**
 * Modal footer container
 */
const ModalFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-row justify-end space-x-2', 'mt-6', className)}
    {...props}
  />
);
ModalFooter.displayName = 'ModalFooter';

/**
 * Modal title with proper ARIA attributes
 */
const ModalTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-base font-medium leading-none tracking-tight',
      'text-fg-primary',
      className
    )}
    {...props}
  />
));
ModalTitle.displayName = DialogPrimitive.Title.displayName;

/**
 * Modal description with proper ARIA attributes
 */
const ModalDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-fg-muted', className)}
    {...props}
  />
));
ModalDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Modal,
  ModalTrigger,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
  ModalClose,
};
